/**
 * Tipos dos itens de contato exibidos no painel de contatos, compartilhados entre o
 * `ContactSearch` (que os monta a partir das respostas do PABX e do Graph) e os favoritos
 * (`useFavorites`/`favoritesApi`), que guardam uma cópia desses mesmos campos.
 *
 * `id` é `string | null` de propósito. Um contato que chega sem id só poderia receber um id
 * sintético baseado na posição na lista, que muda a cada busca — chave instável, inútil para
 * guardar um favorito. Em vez de fabricar essa chave, o item é renderizado normalmente e **sem**
 * estrela.
 */

/** Origem do contato. Separa as duas listas de favoritos: os ids podem colidir entre si. */
export type FavoriteSource = "pabx" | "teams";

/** Contato da agenda do PABX (`contactlist.contacts` — a chave do mapa é o `id`). */
export type PabxContactItem = {
  id: string | null;
  name: string;
  businessNumber: string;
  mobileNumber: string;
  extensionNumber: string;
  homeNumber: string;
};

/** Contato pessoal do Outlook, lido pelo serviço no Graph. */
export type TeamsContactItem = {
  /**
   * Id imutável do Graph: o serviço envia `Prefer: IdType="ImmutableId"` em toda leitura de
   * contatos, então ele sobrevive à troca de pasta dentro da caixa postal. É **case-sensitive**:
   * comparar exatamente, nunca normalizado.
   */
  id: string | null;
  displayName: string;
  mobilePhone: string;
  businessPhones: string[];
};

/** Favorito do PABX, como gravado no backend: id + cópia dos dados do contato. */
export type PabxFavorite = {
  id: string;
  name: string;
  numbers: {
    business: string;
    mobile: string;
    extension: string;
    home: string;
  };
  addedOn?: number;
};

/** Favorito do Teams, como gravado no backend. */
export type TeamsFavorite = {
  id: string;
  displayName: string;
  mobilePhone: string;
  businessPhones: string[];
  addedOn?: number;
};

export type UserFavorites = {
  pabx: PabxFavorite[];
  teams: TeamsFavorite[];
};

export const EMPTY_FAVORITES: UserFavorites = { pabx: [], teams: [] };
